import { Link } from "react-router-dom";
import { motion } from "framer-motion";

export default function Solutions() {
    return (
        <main className="bg-gradient-to-b from-slate-900 to-black text-gray-100 py-16 overflow-hidden">
            <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 space-y-20">
                {/* 🔹 Header */}
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="text-center"
                >
                    <h1 className="text-4xl sm:text-5xl font-bold text-white">
                        Our Solutions
                    </h1>
                    <p className="mt-4 text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
                        SecureID Technologies delivers flexible software, biometric
                        compliance services, and professional consulting for agencies
                        and enterprises. Choose the option that best fits your
                        organization’s workflow.
                    </p>
                </motion.div>

                {/* 🔹 Products (SaaS) */}
                <motion.section
                    initial={{ opacity: 0, y: 80 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.1 }}
                >
                    <div className="flex flex-col lg:flex-row items-center gap-10">
                        <div className="flex-1">
                            <h2 className="text-3xl font-semibold text-white">
                                Products (SaaS)
                            </h2>
                            <p className="mt-2 text-gray-300">
                                Subscription-based platforms for testing, training, and
                                identity management.
                            </p>

                            <article className="mt-8 rounded-2xl bg-white/5 border border-white/10 p-6 shadow-md hover:shadow-lg transition">
                                <h3 className="text-xl font-semibold text-white">ExamsPro</h3>
                                <p className="mt-2 text-gray-300">
                                    Secure offline/online exam delivery platform with
                                    question banks, proctoring, and candidate tracking.
                                </p>
                                <ul className="mt-3 list-disc pl-5 text-sm text-gray-300 space-y-1">
                                    <li>Offline tablet mode for secure testing</li>
                                    <li>Role-based access and analytics</li>
                                    <li>Encrypted result exports</li>
                                </ul>
                                <Link
                                    to="/solutions/examspro"
                                    className="mt-4 inline-block text-indigo-400 hover:underline font-medium"
                                >
                                    Learn more →
                                </Link>
                            </article>
                        </div>

                        {/* Animated image */}
                        <motion.div
                            className="flex-1 flex justify-center"
                            initial={{ opacity: 0, x: 150 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 1.2, ease: "easeOut" }}
                        >
                            <img
                                src="/Images/solutions-products.png"
                                alt="Solutions products"
                                className="w-[85%] max-w-md rounded-2xl shadow-2xl"
                            />
                        </motion.div>
                    </div>
                </motion.section>

                {/* 🔹 Services (HR & Compliance) */}
                <motion.section
                    initial={{ opacity: 0, y: 80 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.2 }}
                >
                    <div className="flex flex-col lg:flex-row items-center gap-10">
                        {/* Animated image */}
                        <motion.div
                            className="flex-1 flex justify-center order-2 lg:order-1"
                            initial={{ opacity: 0, x: -150 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 1.2, ease: "easeOut" }}
                        >
                            <img
                                src="/Images/solutions-services.png"
                                alt="Solutions services"
                                className="w-[85%] max-w-md rounded-2xl shadow-2xl"
                            />
                        </motion.div>

                        <div className="flex-1 order-1 lg:order-2">
                            <h2 className="text-3xl font-semibold text-white">
                                Services (HR & Compliance)
                            </h2>
                            <p className="mt-2 text-gray-300">
                                Biometric and identity verification solutions built
                                for enterprise compliance and security.
                            </p>

                            <article className="mt-8 rounded-2xl bg-white/5 border border-white/10 p-6 shadow-md hover:shadow-lg transition">
                                <h3 className="text-xl font-semibold text-white">
                                    DactylScan Live Scan
                                </h3>
                                <p className="mt-2 text-gray-300">
                                    Windows-based Live Scan app with FDLE-compliant
                                    fingerprint workflows, EBTS packaging, and
                                    signature capture.
                                </p>
                                <ul className="mt-3 list-disc pl-5 text-sm text-gray-300 space-y-1">
                                    <li>Integration with Futronic & SecuGen SDKs</li>
                                    <li>FDLE / FBI-ready formatting</li>
                                    <li>Electronic consent with audit trail</li>
                                </ul>
                                <Link
                                    to="/solutions/dactylscan"
                                    className="mt-4 inline-block text-indigo-400 hover:underline font-medium"
                                >
                                    Learn more →
                                </Link>
                            </article>
                        </div>
                    </div>
                </motion.section>

                {/* 🔹 Contracting (Professional Services) */}
                <motion.section
                    initial={{ opacity: 0, y: 80 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.3 }}
                >
                    <div className="flex flex-col lg:flex-row items-center gap-10">
                        <div className="flex-1">
                            <h2 className="text-3xl font-semibold text-white">
                                Contracting (Professional Services)
                            </h2>
                            <p className="mt-2 text-gray-300">
                                Specialist consulting and development services for
                                enterprise communication and automation.
                            </p>

                            <article className="mt-8 rounded-2xl bg-white/5 border border-white/10 p-6 shadow-md hover:shadow-lg transition">
                                <h3 className="text-xl font-semibold text-white">
                                    Avaya Orchestration Designer Development
                                </h3>
                                <p className="mt-2 text-gray-300">
                                    Contractor services for Avaya Aura® platforms —
                                    custom IVR flow design, web service integrations,
                                    and system optimization.
                                </p>
                                <ul className="mt-3 list-disc pl-5 text-sm text-gray-300 space-y-1">
                                    <li>Speech-enabled IVR applications</li>
                                    <li>CTI and web service integration</li>
                                    <li>Performance monitoring and analytics</li>
                                </ul>
                                <Link
                                    to="/solutions/avaya"
                                    className="mt-4 inline-block text-indigo-400 hover:underline font-medium"
                                >
                                    Learn more →
                                </Link>
                            </article>
                        </div>

                        {/* Animated image */}
                        <motion.div
                            className="flex-1 flex justify-center"
                            initial={{ opacity: 0, x: 150 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 1.2, ease: "easeOut" }}
                        >
                            <img
                                src="/Images/solutions-contracting.png"
                                alt="Solutions contracting"
                                className="w-[85%] max-w-md rounded-2xl shadow-2xl"
                            />
                        </motion.div>
                    </div>
                </motion.section>

                {/* Back link */}
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="text-center text-sm text-gray-400"
                >
                    <Link to="/" className="text-indigo-400 hover:underline">
                        ← Back to Home
                    </Link>
                </motion.p>
            </section>
        </main>
    );
}
